/**
 * domains/market/engines/stateTransition/stateTransitionEngine.ts — MER Algo Footprints v7.1H
 *
 * State Transition Layer — public entry point.
 *
 * Sits BETWEEN analytical engines and final UI/recommendation output.
 * Converts raw signals into an explicit, finite MarketState with
 * actionability, triggers, and operator guidance.
 *
 * Usage:
 *   const result = evaluateStateTransition(ctx);
 *   // ctx.stateTransition = result (added to SymbolContext)
 */

import {
  StateTransitionResult, Actionability, StateTriggers,
  PENDING_MIN_COMPOSITE,
  STATE_ACTION_LABEL, STATE_TRANSITION_ENGINE_VERSION,
} from './types';
import {
  extractSignals, computeScoreFactors, evaluateAllRules, selectPrimaryState, SignalSummary,
} from './rules';
import type {
  SMCResult, RegimeResult, TrapSignal, LiquidityZone, TrendResult, GammaResult,
} from '../../../../shared/types';
import type { TradeSetup } from '../../../../shared/types';

// ─── Trigger builder ──────────────────────────────────────────────────────────

function buildTriggers(
  state:  import('./types').MarketState,
  s:      SignalSummary,
  zones:  LiquidityZone[],
  setups: TradeSetup[],
  curPrice: number,
): StateTriggers {
  const setup = setups[0] ?? null;

  // Nearest resistance / support for trigger text
  const nearRes = zones.filter(z => z.type === 'resistance').sort((a, b) => a.distPct - b.distPct)[0];
  const nearSup = zones.filter(z => z.type === 'support').sort((a, b) => a.distPct - b.distPct)[0];
  const resStr = nearRes ? `${nearRes.price.toFixed(2)} (${nearRes.label})` : null;
  const supStr = nearSup ? `${nearSup.price.toFixed(2)} (${nearSup.label})` : null;

  switch (state) {
    case 'COMPRESSION_BREAKOUT_PENDING':
      return {
        longTrigger:  resStr ? `Acceptance above ${resStr} with volume expansion` : 'Acceptance above resistance with volume',
        shortTrigger: supStr ? `Loss of ${supStr} equal lows with close below` : 'Loss of support with acceptance below',
        confirmation: ['Displacement candle in direction', 'RVOL ≥ 1.5× on breakout bar'],
        failure:      ['Rejection at level without acceptance', 'Immediate reversal after test'],
      };

    case 'PULLBACK_TO_SUPPORT_PENDING':
      return {
        longTrigger: supStr
          ? `Pullback into ${supStr} → hold + reclaim + bullish displacement`
          : 'Pullback into support → hold + reclaim',
        confirmation: ['Bullish BOS or CHoCH on pullback', 'VWAP reclaim if below'],
        failure:      ['Support loss / close below', 'VWAP rejection on attempted reclaim'],
      };

    case 'PULLBACK_TO_RESISTANCE_PENDING':
      return {
        shortTrigger: resStr
          ? `Bounce into ${resStr} → failure + rejection + bearish displacement`
          : 'Bounce into resistance → rejection',
        confirmation: ['Bearish CHoCH on bounce', 'VWAP rejection'],
        failure:      ['Resistance clear with acceptance above', 'Sustained hold above level'],
      };

    case 'BREAKOUT_CONFIRMED_LONG':
      return {
        longTrigger: setup?.entry ? `Entry near ${setup.entry}` : 'Pullback to BOS level for continuation',
        confirmation: ['RVOL expansion sustained', 'No bearish displacement on pullback'],
        failure:      [
          setup?.invalidation ?? 'Close below BOS level',
          'Displacement back below breakout',
        ],
      };

    case 'BREAKDOWN_CONFIRMED_SHORT':
      return {
        shortTrigger: setup?.entry ? `Entry near ${setup.entry}` : 'Bounce to BOS level for continuation',
        confirmation: ['RVOL expansion sustained', 'No bullish displacement on bounce'],
        failure:      [
          setup?.invalidation ?? 'Close above BOS level',
          'Displacement back above breakdown',
        ],
      };

    case 'TRAP_REVERSAL_RISK':
      return {
        longTrigger:  s.trapSide === 'bear' ? 'Sweep of low + BOS confirmation + reclaim' : undefined,
        shortTrigger: s.trapSide === 'bull' ? 'Sweep of high + CHoCH + displacement down' : undefined,
        confirmation: ['CHoCH or BOS in reversal direction', 'RVOL spike on reversal candle'],
        failure:      ['Sweep level reclaimed in original direction', 'New BOS in trap direction'],
      };

    case 'TREND_CONTINUATION_ACTIVE':
      return {
        longTrigger:  s.smcBias === 'bullish' ? (setup?.entry ?? 'Near VWAP or last BOS') : undefined,
        shortTrigger: s.smcBias === 'bearish' ? (setup?.entry ?? 'Near VWAP or last BOS') : undefined,
        confirmation: setup?.confirmation ? [setup.confirmation] : ['Displacement aligned + RVOL ≥ 1.2×'],
        failure:      [setup?.invalidation ?? 'CHoCH against trend direction', 'VWAP loss with acceptance below/above'],
      };

    case 'EXHAUSTION_RISK':
      return {
        longTrigger:  s.smcBias === 'bearish' ? 'Support hold + bullish BOS for mean-reversion long' : undefined,
        shortTrigger: s.smcBias === 'bullish' ? 'Resistance rejection + bearish CHoCH for fade short' : undefined,
        confirmation: ['Displacement in reversal direction', 'VWAP rejection on extended move'],
        failure:      ['New extension high/low with acceptance', 'Gamma pinning at current level'],
      };

    default:
      return {};
  }
}

// ─── Operator guidance ────────────────────────────────────────────────────────

function buildOperatorGuidance(
  state: import('./types').MarketState,
  s:     SignalSummary,
  scoreFactors: ReturnType<typeof computeScoreFactors>,
): string {
  const action = STATE_ACTION_LABEL[state];
  const trapWarn = s.trapSeverity !== 'None' ? ` | ⚠ ${s.trapSeverity} trap: ${s.trapSide}` : '';
  const gammaNote = s.gammaExpansion ? ' | Short gamma — expansion active' : '';

  switch (state) {
    case 'NO_VALID_SETUP':
      return `${action} — conflicting or insufficient signals. No clean setup path.${trapWarn}`;
    case 'COMPRESSION_BREAKOUT_PENDING':
      return `${action} — price compressed at key level. Await directional resolution before entry.${trapWarn}${gammaNote}`;
    case 'PULLBACK_TO_SUPPORT_PENDING':
      return `${action} — bullish structure intact but entry is late. Wait for pullback to support.`;
    case 'PULLBACK_TO_RESISTANCE_PENDING':
      return `${action} — bearish structure intact but entry is late. Wait for bounce to resistance.`;
    case 'BREAKOUT_CONFIRMED_LONG':
      return `${action} — resistance cleared and accepted. Continuation entry on pullback.${gammaNote}`;
    case 'BREAKDOWN_CONFIRMED_SHORT':
      return `${action} — support lost and accepted. Continuation entry on bounce.${gammaNote}`;
    case 'TRAP_REVERSAL_RISK':
      return `${action} — active trap signal. Directional continuation suspect. ${trapWarn}.`;
    case 'RANGE_ROTATION':
      return `${action} — no directional edge. Price likely rotating within range.`;
    case 'TREND_CONTINUATION_ACTIVE':
      return `${action} — aligned trend, structure, and displacement. Clean continuation window.${gammaNote}`;
    case 'EXHAUSTION_RISK':
      return `${action} — trend extended, reversal risk elevated. Avoid chasing.${trapWarn}`;
    default:
      return action;
  }
}

// ─── Actionability mapping ────────────────────────────────────────────────────

function deriveActionability(
  state:     import('./types').MarketState,
  composite: number,
): Actionability {
  const avoidStates = new Set(['NO_VALID_SETUP', 'EXHAUSTION_RISK', 'TRAP_REVERSAL_RISK', 'RANGE_ROTATION']);
  const actionableStates = new Set(['BREAKOUT_CONFIRMED_LONG', 'BREAKDOWN_CONFIRMED_SHORT', 'TREND_CONTINUATION_ACTIVE']);
  const pendingStates = new Set([
    'COMPRESSION_BREAKOUT_PENDING', 'PULLBACK_TO_SUPPORT_PENDING',
    'PULLBACK_TO_RESISTANCE_PENDING',
  ]);

  if (avoidStates.has(state)) return 'avoid';
  if (state === 'TRAP_REVERSAL_RISK') return 'avoid';

  // Breakout/breakdown/continuation: confirmed structural states need lower composite floor
  // because price has moved through the liquidity zone (lc is naturally lower post-breakout)
  const confirmedStateMin = 35;
  if (actionableStates.has(state) && composite >= confirmedStateMin) return 'actionable';
  if (pendingStates.has(state)) return 'pending';

  return composite >= PENDING_MIN_COMPOSITE ? 'pending' : 'avoid';
}

// ─── Invalidation conditions ──────────────────────────────────────────────────

function buildInvalidations(state: import('./types').MarketState, s: SignalSummary): string[] {
  const inv: string[] = [];

  if (state === 'BREAKOUT_CONFIRMED_LONG' || state === 'TREND_CONTINUATION_ACTIVE') {
    inv.push('Close below BOS level with bearish displacement');
    inv.push('CHoCH against bullish direction');
  }
  if (state === 'BREAKDOWN_CONFIRMED_SHORT' || state === 'TREND_CONTINUATION_ACTIVE') {
    inv.push('Close above BOS level with bullish displacement');
    inv.push('CHoCH against bearish direction');
  }
  if (state === 'COMPRESSION_BREAKOUT_PENDING') {
    inv.push('Range expansion in either direction with acceptance — state resolves to breakout or breakdown');
  }
  if (state === 'PULLBACK_TO_SUPPORT_PENDING') {
    inv.push('Support failure / close below with displacement');
    inv.push('Aggressive displacement against bullish direction before pullback');
  }
  if (state === 'PULLBACK_TO_RESISTANCE_PENDING') {
    inv.push('Resistance clear with bullish acceptance — state resolves to breakout');
    inv.push('New bearish displacement without bounce');
  }
  if (state === 'TRAP_REVERSAL_RISK') {
    inv.push('Trap level reclaimed and accepted in original direction');
    inv.push('Fresh BOS in continuation direction confirms no reversal');
  }
  if (state === 'EXHAUSTION_RISK') {
    inv.push('Fresh displacement with new BOS restarts momentum');
    inv.push('Pullback into structural support held — reload opportunity');
  }

  return inv.length > 0 ? inv : ['Significant structural change in any direction'];
}

// ─── Main engine ──────────────────────────────────────────────────────────────

export function evaluateStateTransition(
  smc:     SMCResult | null,
  regime:  RegimeResult | null,
  traps:   TrapSignal[],
  zones:   LiquidityZone[],
  trend:   TrendResult | null,
  gamma:   GammaResult | null,
  setups:  TradeSetup[],
  candles_len:     number,
  curPrice:        number,
  setupActionable: boolean,
  setupConfidence: number,
): StateTransitionResult {
  const signals      = extractSignals(smc, regime, traps, zones, trend, gamma, candles_len, curPrice, setupActionable, setupConfidence);
  const scoreFactors = computeScoreFactors(signals);
  const candidates   = evaluateAllRules(signals, scoreFactors);
  const primary      = selectPrimaryState(candidates);

  const actionability = deriveActionability(primary.state, scoreFactors.composite);
  const triggers      = buildTriggers(primary.state, signals, zones, setups, curPrice);
  const invalidation  = buildInvalidations(primary.state, signals);
  const guidance      = buildOperatorGuidance(primary.state, signals, scoreFactors);

  // Context flags — observable conditions that contributed
  const contextFlags: string[] = [];
  if (signals.atResistance)    contextFlags.push('At resistance');
  if (signals.atSupport)       contextFlags.push('At support');
  if (signals.hasEqualHighs)   contextFlags.push('Equal highs near price');
  if (signals.hasEqualLows)    contextFlags.push('Equal lows near price');
  if (signals.sweepReclaim)    contextFlags.push('Sweep reclaimed');
  if (signals.gammaExpansion)  contextFlags.push('Short gamma / expansion');
  if (signals.trendMature)     contextFlags.push('Trend mature');
  if (signals.dispFresh)       contextFlags.push(`Fresh ${signals.dispDirection} displacement`);
  if (signals.trapSeverity !== 'None') contextFlags.push(`${signals.trapSeverity} trap (${signals.trapSide})`);

  // Normalized quality and confidence
  const setupQuality = Math.round(scoreFactors.composite);
  const confidence   = Math.min(100, Math.round(
    (scoreFactors.structuralClarity * 0.4) +
    (scoreFactors.liquidityClarity   * 0.3) +
    (scoreFactors.triggerClarity     * 0.3)
  ));

  return {
    state:           primary.state,
    actionability,
    directionalBias: primary.bias,
    setupQuality,
    confidence,
    rationale:       primary.rationale,
    invalidation,
    triggers,
    contextFlags,
    scoreFactors,
    operatorGuidance: guidance,
    engineVersion:   STATE_TRANSITION_ENGINE_VERSION,
    evaluatedAt:     Date.now(),
  };
}
